/**
 * The superuser console — `/platform/*`. Every route is refused with 403 for
 * anyone who is not a superuser; nothing here is organization-scoped.
 */

/** A paginated platform list (25 a page unless a caller asks for up to 100). */
export interface PlatformPage<T> {
  total_pages: number
  current_page: number
  total_items: number
  results: T[]
}

export interface PageParams {
  page?: number
  page_size?: number
  search?: string
}
export interface PlanListParams extends PageParams {
  category?: string
  is_active?: boolean
  is_free?: boolean
  is_purchasable?: boolean
}
export interface FeatureListParams extends PageParams {
  is_active?: boolean
}
export interface AccessibilityListParams extends PageParams {
  group?: string
}
export interface SubscriptionListParams extends PageParams {
  status?: string
  plan?: string
  organization?: string
}
export interface InvoiceListParams extends PageParams {
  organization?: string
  payment_status?: string
}
export interface PaymentListParams extends PageParams {
  organization?: string
  status?: string
}
export interface SettingListParams extends PageParams {
  category?: string
  value_type?: string
  is_active?: boolean
  is_sensitive?: boolean
}
export interface SettingChoiceListParams extends PageParams {
  is_active?: boolean
}
export interface PlatformUserListParams extends PageParams {
  is_superuser?: boolean
  two_factor_enrolled?: boolean
  two_factor_override?: 'inherit' | 'required' | 'exempt'
}

export type PlatformListParams =
  | PageParams
  | PlanListParams
  | FeatureListParams
  | AccessibilityListParams
  | SubscriptionListParams
  | InvoiceListParams
  | PaymentListParams
  | SettingListParams
  | SettingChoiceListParams
  | PlatformUserListParams

/* ------------------------------------------------------------------ plans */

export interface PlanCategory {
  id: string
  name: string
  order: number
  description: string | null
  features: string[]
  features_fa: string[]
  created_at: string
  last_update: string
}
export interface PlanCategoryWrite {
  name: string
  order?: number
  description?: string
  features?: string[]
  features_fa?: string[]
}

export interface PlanFeature {
  id: string
  name: string
  description: string
  image: string | null
  icon_name: string | null
  price: string
  is_active: boolean
  /** How many plans advertise this feature; an edit or delete changes it on all of them. */
  plan_count: number
  created_at: string
  last_update: string
}
export interface PlanFeatureWrite {
  name: string
  description: string
  price: string
  icon_name?: string
  is_active?: boolean
  /** A new file uploads as multipart; `null` clears the stored image. */
  image?: File | null
}

export interface AccessibilityGroup {
  id: string
  name: string
  description: string | null
  created_at: string
  last_update: string
}
export interface AccessibilityGroupWrite {
  name: string
  description?: string
}
export interface Accessibility {
  id: string
  name: string
  description: string | null
  group: string
  created_at: string
  last_update: string
}
export interface AccessibilityWrite {
  name: string
  group: string
  description?: string
}
export type AccessibilityStatus = 'active' | 'on-demand'
export interface PlanAccessibility {
  accessibility: string
  status: AccessibilityStatus
  name?: string
}

/** `value: null` is "unlimited". */
export interface ResourceLimit {
  value: number | null
  window_type: string
}
export type ResourceLimits = Record<string, ResourceLimit>

export interface Plan {
  id: string
  category: string | null
  order: number
  name: string
  name_fa: string | null
  /** A decimal string; shown as sent. */
  price: string
  duration_days: number
  scan_limit: number | null
  description: string | null
  description_fa: string | null
  features: string[]
  general_features: string[]
  general_features_fa: string[]
  permissions: string[]
  components: string[]
  resource_limits: ResourceLimits
  is_free: boolean
  is_purchasable: boolean
  is_active: boolean
  active_subscribers: number
  warnings: string[]
  accessibilities: PlanAccessibility[]
  created_at: string
  last_update: string
}
export interface PlanWrite {
  category?: string | null
  order?: number
  name: string
  name_fa?: string
  price: string
  duration_days: number
  scan_limit?: number | null
  description?: string
  description_fa?: string
  features?: string[]
  general_features?: string[]
  general_features_fa?: string[]
  accessibilities?: Array<Pick<PlanAccessibility, 'accessibility' | 'status'>>
  permissions?: string[]
  components?: string[]
  resource_limits?: ResourceLimits
  is_free?: boolean
  is_purchasable?: boolean
  is_active?: boolean
}

export interface LabelledOption {
  value: string
  label: string
}
export interface PermissionOption extends LabelledOption {
  resource: string
}
/** `GET /platform/plans/form-options/` */
export interface PlanFormOptions {
  permissions: PermissionOption[]
  components: LabelledOption[]
  /** False when the component catalog could not be reached: the picker says so. */
  components_catalog_reachable: boolean
  window_types: LabelledOption[]
  resource_types: string[]
  accessibility_statuses: AccessibilityStatus[]
}

/* ---------------------------------------------------------- subscriptions */

export type SubscriptionStatus = 'active' | 'inactive' | 'past_due' | 'canceled' | 'expired'
export interface PlatformRef {
  id: string
  name: string
}
export interface PlatformUserRef {
  id: string
  email: string
}
export interface PlatformSubscription {
  id: string
  organization: PlatformRef | null
  user: PlatformUserRef | null
  plan: PlatformRef
  status: SubscriptionStatus
  start_date: string
  end_date: string
  canceled_at: string | null
  permissions: string[]
  components: string[]
  resource_limits: ResourceLimits
  last_synced_at: string | null
  created_at: string
  last_update: string
}
export type OperationSummary = Record<string, number | boolean | Record<string, number>>
export interface SubscriptionOperationResult {
  subscription: PlatformSubscription
  summary: OperationSummary
  /** The operation SUCCEEDED; each entry is a non-blocking notice. */
  warnings: string[]
}
export interface SubscriptionCreate {
  organization: string
  plan: string
  start_date?: string
  end_date?: string
  cancel_existing?: boolean
}
export interface SubscriptionPatch {
  end_date?: string
  permissions?: string[]
  components?: string[]
  resource_limits?: ResourceLimits
}
export interface PlatformOrganization {
  id: string
  name: string
  slug: string
  is_active: boolean
  active_subscription: { id: string; plan_name: string; end_date: string } | null
}

/* ---------------------------------------------------------------- billing */

export interface PlatformInvoice {
  id: string
  organization: PlatformRef | null
  user: PlatformUserRef | null
  item: { type: string; id: string; name: string | null } | null
  amount: string
  discount: string | null
  discount_amount: string
  final_amount: string
  payment_status: string
  billing_period_start: string
  billing_period_end: string
  expiry_date: string | null
  created_at: string
}
export interface PlatformPayment {
  id: string
  invoice: string
  amount: string
  status: string
  payment_method: string
  transaction_id: string | null
  created_at: string
}

/* --------------------------------------------------------------- settings */

export type SettingValueType = 'string' | 'integer' | 'float' | 'boolean' | 'json' | 'choice'
export type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue }
export type SettingValue = JsonValue

export interface PlatformSetting {
  id: number
  key: string
  category: string
  /** `null` when the setting is sensitive: the API never sends it. */
  value: SettingValue
  has_value: boolean
  value_type: SettingValueType
  description: string
  is_active: boolean
  is_sensitive: boolean
  available_choices: number[]
  created_at: string
  updated_at: string
}
export interface SettingWrite {
  key?: string
  category?: string
  value_type?: SettingValueType
  value?: SettingValue
  description?: string
  is_active?: boolean
  is_sensitive?: boolean
  available_choices?: number[]
}
export interface SettingHistoryEntry {
  id: number
  old_value: string | null
  new_value: string | null
  changed_by: string
  changed_at: string
}
export interface SettingFormOptions {
  keys: LabelledOption[]
  categories: LabelledOption[]
  value_types: Array<{ value: SettingValueType; label: string }>
}
export interface SettingChoice {
  id: number
  choice_value: string
  display_name: string
  description: string
  is_active: boolean
  order: number
  created_at: string
  updated_at: string
}
export interface SettingChoiceWrite {
  choice_value: string
  display_name: string
  description?: string
  is_active?: boolean
  order?: number
}
export interface ClearCacheResult {
  cleared: number
}

/* ----------------------------------------------------------------- errors */

export const PLATFORM_ERROR_CODES = [
  'free_plan_exists',
  'plan_not_purchasable',
  'no_owner_role',
  'invalid_dates',
  'same_plan',
  'no_organization',
  'subscription_not_active',
  'permission_grant_failed',
  'in_use',
  'privileged_user_cannot_be_exempt',
  'active_subscription_exists',
] as const
export type PlatformErrorCode = (typeof PLATFORM_ERROR_CODES)[number]

/* ------------------------------------------------- two-factor administration */

export type TwoFactorMode = 'disabled' | 'optional' | 'required'
export type TwoFactorOverride = 'required' | 'exempt'

/** `GET /platform/two-factor/` */
export interface PlatformTwoFactorMode {
  mode: TwoFactorMode
  enrolled_users: number
  required_overrides: number
  exempt_overrides: number
}
/** `PUT /platform/two-factor/` */
export interface PlatformTwoFactorModeSaved extends PlatformTwoFactorMode {
  sessions_revocation_queued: boolean
}
export interface PlatformUserTwoFactor {
  enrolled: boolean
  override: TwoFactorOverride | null
  effective: 'disabled' | 'optional' | 'required'
}
export interface PlatformUser {
  id: string
  email: string
  is_active: boolean
  is_staff: boolean
  is_superuser: boolean
  two_factor: PlatformUserTwoFactor
}
export interface PlatformUserTwoFactorDetail {
  enrolled: boolean
  confirmed_at: string | null
  recovery_codes_remaining: number
  override: TwoFactorOverride | null
  effective: 'disabled' | 'optional' | 'required'
  reason: string
  updated_by: string | null
  updated_at: string | null
}
export interface PlatformUserTwoFactorPatch {
  override: TwoFactorOverride | null
  reason: string
}
